// S2 (v0.13.0) — super admin: tenant detail.
import { useState } from "react";
import { useParams } from "react-router-dom";

import {
  useAdminTenant,
  useReactivateTenant,
  useSuspendTenant,
  useUpdateTenant,
} from "@/api/adminTenants";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { QueryState } from "@/components/QueryState";

const PLANS = ["basic", "professional", "enterprise", "banking"] as const;

export default function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: t, isLoading, isError, error, refetch } = useAdminTenant(id);
  const update = useUpdateTenant(id ?? "");
  const suspend = useSuspendTenant();
  const reactivate = useReactivateTenant();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [plan, setPlan] = useState<(typeof PLANS)[number]>("basic");
  const [expiresAt, setExpiresAt] = useState("");

  function startEdit() {
    if (!t) return;
    setName(t.name);
    setPlan(t.plan as (typeof PLANS)[number]);
    setExpiresAt(t.expires_at ? t.expires_at.slice(0, 10) : "");
    setEditing(true);
  }

  async function save() {
    await update.mutateAsync({
      name,
      plan,
      expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
    });
    setEditing(false);
  }

  // T1a — `isLoading || !t` renders "Loading…" FOREVER on a failed fetch:
  // React Query sets isLoading=false and t=undefined, so this branch stays
  // true and nothing ever says the request failed. A spinner that never
  // resolves is the same false signal as an empty table.
  if (isError) {
    return (
      <QueryState
        isLoading={false}
        isError
        error={error}
        label="this tenant"
        onRetry={refetch}
      >
        {null}
      </QueryState>
    );
  }
  if (isLoading || !t) return <p className="text-sm">Loading…</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t.name}</h1>
          <p className="text-sm text-muted-foreground">
            <span className="font-mono">{t.slug}</span> ·{" "}
            <Badge variant="online">{t.plan}</Badge>{" "}
            <Badge
              variant={
                t.status === "active"
                  ? "online"
                  : t.status === "suspended"
                    ? "error"
                    : "offline"
              }
            >
              {t.status}
            </Badge>
          </p>
        </div>
        <div className="flex gap-2">
          {!editing && (
            <Button variant="outline" onClick={startEdit}>
              Edit
            </Button>
          )}
          {t.status === "active" ? (
            <Button
              variant="destructive"
              onClick={() => {
                // A9/AU-200 — tenant suspend is more destructive than user
                // suspend (every user in the tenant loses access); confirm first.
                if (
                  window.confirm(
                    `Suspend tenant "${t.name}"? Every user in this tenant ` +
                      `will lose access until it is reactivated.`,
                  )
                )
                  suspend.mutate(t.id);
              }}
            >
              Suspend
            </Button>
          ) : (
            <Button onClick={() => reactivate.mutate(t.id)}>Reactivate</Button>
          )}
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <Card>
          <CardContent className="space-y-2 p-4">
            <p className="text-xs uppercase text-muted-foreground">Cameras</p>
            <p className="text-3xl font-semibold">{t.camera_count}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="space-y-2 p-4">
            <p className="text-xs uppercase text-muted-foreground">Users</p>
            <p className="text-3xl font-semibold">{t.user_count}</p>
          </CardContent>
        </Card>
      </div>

      {editing && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <div>
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label>Plan</Label>
              <select
                value={plan}
                onChange={(e) => setPlan(e.target.value as (typeof PLANS)[number])}
                className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
              >
                {PLANS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Expires</Label>
              <Input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button onClick={save} disabled={update.isPending}>
                Save
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="space-y-2 p-4">
          <p className="text-xs uppercase text-muted-foreground">
            Settings JSON
          </p>
          <pre className="overflow-auto rounded bg-muted/50 p-2 text-xs">
            {JSON.stringify(t.settings_json ?? {}, null, 2)}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}
